import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import type { featuredCourses as FeaturedList } from "@/content/courses";

/** Drift speed in px per second. Positive = cards travel left to right. */
export const DRIFT_PX_PER_SECOND = 20;
/** Delay before the drift resumes after a drag ends (ms). */
const RESUME_DELAY_MS = 1000;
/** Pointer travel (px) beyond which a press counts as a drag, not a click. */
const DRAG_THRESHOLD_PX = 5;
/** Per-frame (60fps) decay factor for release inertia. */
const INERTIA_DECAY = 0.92;
/** Gap between cards in px (matches gap-5). */
const GAP_PX = 20;

type Course = (typeof FeaturedList)[number];

/**
 * Catalog-page-only featured row: a slow continuous drift (rAF + transform),
 * pointer drag with light inertia, and a seamless loop built from repeated
 * copies of the set. Copy index 1 is the real, focusable one; the rest are
 * aria-hidden, inert clones.
 */
export function CatalogFeaturedCarousel({ courses }: { courses: Course[] }) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLUListElement>(null);
  const [copies, setCopies] = useState(3);

  const offset = useRef(0); // in [0, setWidth)
  const setWidth = useRef(0);
  const hover = useRef(false);
  const focusWithin = useRef(false);
  const dragging = useRef(false);
  const resumeAt = useRef(0);
  const velocity = useRef(0); // px per ms, inertia after release
  const reduceMotion = useRef(false);
  const drag = useRef({ id: -1, startX: 0, lastX: 0, lastT: 0, moved: false });
  const suppressClick = useRef(false);

  // Measure one set's width and how many copies cover the viewport.
  useEffect(() => {
    const vp = viewportRef.current;
    const track = trackRef.current;
    if (!vp || !track) return;
    const measure = () => {
      const first = track.querySelector<HTMLElement>('[data-copy="0"]');
      const next = track.querySelector<HTMLElement>('[data-copy="1"]');
      if (!first || !next) return;
      const w = next.offsetLeft - first.offsetLeft;
      if (w <= 0) return;
      setWidth.current = w;
      offset.current = ((offset.current % w) + w) % w;
      setCopies(Math.max(3, Math.ceil(vp.clientWidth / w) + 2));
      apply();
    };
    const ro = new ResizeObserver(measure);
    ro.observe(vp);
    ro.observe(track);
    measure();
    return () => ro.disconnect();
  }, [courses.length]);

  const apply = () => {
    const track = trackRef.current;
    if (!track) return;
    track.style.transform = `translate3d(${offset.current - setWidth.current}px,0,0)`;
  };

  const moveBy = (dx: number) => {
    const w = setWidth.current;
    if (!w) return;
    offset.current = (((offset.current + dx) % w) + w) % w;
    apply();
  };

  // Animation loop.
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    reduceMotion.current = mq.matches;
    const onMq = () => (reduceMotion.current = mq.matches);
    mq.addEventListener("change", onMq);

    let raf = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = Math.min(now - last, 100);
      last = now;
      if (!dragging.current) {
        if (Math.abs(velocity.current) > 0.01) {
          moveBy(velocity.current * dt);
          velocity.current *= Math.pow(INERTIA_DECAY, dt / 16.67);
        } else {
          velocity.current = 0;
        }
        const paused = hover.current || focusWithin.current || now < resumeAt.current;
        if (!paused && !reduceMotion.current) {
          moveBy((DRIFT_PX_PER_SECOND * dt) / 1000);
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      mq.removeEventListener("change", onMq);
    };
  }, []);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    dragging.current = true;
    velocity.current = 0;
    suppressClick.current = false;
    drag.current = { id: e.pointerId, startX: e.clientX, lastX: e.clientX, lastT: e.timeStamp, moved: false };
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!dragging.current || e.pointerId !== d.id) return;
    if (!d.moved && Math.abs(e.clientX - d.startX) > DRAG_THRESHOLD_PX) {
      d.moved = true;
      // Capture only once it's a real drag, so a plain click still hits the link.
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    const dx = e.clientX - d.lastX;
    const dt = Math.max(e.timeStamp - d.lastT, 1);
    moveBy(dx);
    velocity.current = dx / dt;
    d.lastX = e.clientX;
    d.lastT = e.timeStamp;
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!dragging.current || e.pointerId !== d.id) return;
    dragging.current = false;
    suppressClick.current = d.moved;
    if (!d.moved || e.timeStamp - d.lastT > 80) velocity.current = 0;
    velocity.current = Math.max(-3, Math.min(3, velocity.current));
    resumeAt.current = performance.now() + RESUME_DELAY_MS;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  };

  return (
    <div
      ref={viewportRef}
      role="region"
      aria-roledescription="carousel"
      aria-label="Featured courses"
      className="relative w-full cursor-grab touch-pan-y select-none overflow-hidden py-3 active:cursor-grabbing"
      onPointerEnter={(e) => e.pointerType === "mouse" && (hover.current = true)}
      onPointerLeave={(e) => e.pointerType === "mouse" && (hover.current = false)}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onFocus={(e) => {
        // Only keyboard focus pauses; a mouse press also focuses the link.
        focusWithin.current = (e.target as HTMLElement).matches(":focus-visible");
      }}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) focusWithin.current = false;
      }}
      onClickCapture={(e) => {
        if (suppressClick.current) {
          e.preventDefault();
          e.stopPropagation();
          suppressClick.current = false;
        }
      }}
      onDragStart={(e) => e.preventDefault()}
    >
      <ul
        ref={trackRef}
        className="flex w-max will-change-transform"
        style={{ gap: GAP_PX, paddingLeft: 0 }}
      >
        {Array.from({ length: copies }).flatMap((_, copy) =>
          courses.map((c, i) => {
            const isClone = copy !== 1;
            return (
              <li
                key={`${copy}-${c.slug}`}
                data-copy={i === 0 ? copy : undefined}
                aria-hidden={isClone || undefined}
                inert={isClone || undefined}
                className="flex shrink-0 w-[calc((100vw-48px)*0.72)] md:w-[calc((min(100vw,1152px)*0.266667-25.6px)*0.9)]"
              >
                <Link
                  to="/course-catalog/$slug/"
                  params={{ slug: c.slug }}
                  tabIndex={isClone ? -1 : undefined}
                  draggable={false}
                  className="group relative flex w-full flex-col overflow-hidden rounded-2xl border border-border/60 bg-background transition-all hover:-translate-y-1 hover:border-primary/30 hover:shadow-[0_10px_30px_-8px_rgba(0,0,0,0.15)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                >
                  <div className="aspect-square w-full overflow-hidden bg-lavender">
                    {c.image ? (
                      <img
                        src={c.image}
                        alt=""
                        width={400}
                        height={400}
                        draggable={false}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="h-full w-full gradient-brand opacity-80" />
                    )}
                  </div>
                  <div className="flex flex-1 flex-col px-[25px] py-[18px]">
                    <h3 className="font-display text-base leading-snug text-ink">{c.title}</h3>
                    {c.tagline && <p className="mt-3 text-sm text-muted-foreground">{c.tagline}</p>}
                    <span className="mt-auto inline-flex items-center gap-1.5 pt-3 text-sm font-semibold text-primary">
                      View course
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </li>
            );
          }),
        )}
      </ul>
    </div>
  );
}
